import * as React from 'react';

import {
  useDeclareSkill,
  useDeclaredSkills,
  useRemoveSkill,
  useSkillReferential,
} from '@/api/use-rate-collection';
import type { ProfileSkillDto } from '@/api/rate-collection-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// =============================================================================
// skills-panel.tsx — Déclaration de COMPÉTENCES par profil (S13, D2 / G0-S13-7).
// =============================================================================
// Granularité PROFIL (G0-S13-7) : pour chaque profil, le fournisseur coche/décoche les
// compétences qu'il déclare. PUT skills (upsert idempotent) / DELETE skills (idempotent),
// liste re-fetchée après mutation (jamais d'état local — RULE_REF_006).
//
// D4 SEAM : le référentiel COURANT (compétences déclarables par profil) est CÂBLÉ au GET
// skill-referential (useSkillReferential) — plus aucune prop de données. L'API résout la liste
// pour la version de catalogue figée de la submission (un profil sans compétence déclarable est
// OMIS). Les compétences DÉJÀ déclarées restent servies par useDeclaredSkills (autorité de la coche).
//
// a11y WCAG 2.1 AA : chaque case = <input type=checkbox> labellisée (label associé par `htmlFor`),
// groupée par profil sous un <fieldset>/<legend> (navigation clavier + lecteur d'écran). L'état
// coché est porté par l'attribut natif `checked` (jamais une couleur seule).

export interface SkillsPanelProps {
  readonly submissionId: string;
  /** Désactive les cases (grille verrouillée / deadline dépassée). */
  readonly disabled: boolean;
}

export function SkillsPanel({
  submissionId,
  disabled,
}: SkillsPanelProps): React.JSX.Element | null {
  const referentialQuery = useSkillReferential(submissionId);
  const skillsQuery = useDeclaredSkills(submissionId);
  const declareMutation = useDeclareSkill(submissionId);
  const removeMutation = useRemoveSkill(submissionId);

  // Référentiel courant (profils porteurs de compétences déclarables ; l'API omet les profils vides).
  const profilesWithSkills = referentialQuery.data?.profiles ?? [];

  // Aucun profil porteur de compétences déclarables (ou chargement/échec) : on n'affiche pas le panneau.
  if (profilesWithSkills.length === 0) {
    return null;
  }

  // Ensemble des compétences déclarées (clé `profileCode::skillCode`) depuis l'autorité serveur.
  const declaredSet = new Set<string>(
    (skillsQuery.data?.skills ?? []).map((s) => `${s.profileCode}::${s.skillCode}`),
  );

  const toggle = (profileCode: string, skillCode: string, next: boolean): void => {
    if (next) {
      declareMutation.mutate({ profileCode, skillCode });
    } else {
      removeMutation.mutate({ profileCode, skillCode });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Compétences déclarées par profil</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Cochez les compétences que vous proposez pour chaque profil.
        </p>
        {skillsQuery.isError && (
          <p role="alert" className="text-sm text-destructive">
            Impossible de charger les compétences déclarées. {skillsQuery.error.message}
          </p>
        )}
        {profilesWithSkills.map((profile) => (
          <fieldset
            key={profile.profileCode}
            className="rounded-md border border-border p-3"
            disabled={disabled}
          >
            <legend className="px-1 text-sm font-medium text-foreground">
              {/* REM-A #4 (DBU §3.5) : intitulé LISIBLE (Profile.libelle) ; repli sur le code technique
                  si l'API ne l'a pas renseigné (jamais une légende vide). */}
              {profile.libelle.trim().length > 0 ? profile.libelle : profile.profileCode}
            </legend>
            <div className="mt-1 flex flex-col gap-2">
              {profile.skills.map((skill) => (
                <SkillRow
                  key={skill.skillCode}
                  profileCode={profile.profileCode}
                  skill={skill}
                  checked={declaredSet.has(`${profile.profileCode}::${skill.skillCode}`)}
                  disabled={disabled}
                  onToggle={(next) => toggle(profile.profileCode, skill.skillCode, next)}
                />
              ))}
            </div>
          </fieldset>
        ))}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// SkillRow — case de compétence + AIDE (S35-CLONE-SKILLS-UX #4, DBU §3.5 l.2826).
// =============================================================================
// « … en s'appuyant si nécessaire sur le descriptif riche fourni par l'admin … consultable par survol ou
// clic d'aide. » L'aide est portée par un bouton dédié : `title` (survol) + bascule au clic (aria-expanded)
// révélant le descriptif. À défaut de descriptif (API non encore enrichie — FLAG ORCH), l'affordance reste
// présente et honnête (« Aucun descriptif fourni »), jamais un texte inventé. WCAG : bouton labellisé,
// texte d'aide relié par aria-controls/id, jamais la couleur seule.
function SkillRow({
  profileCode,
  skill,
  checked,
  disabled,
  onToggle,
}: {
  readonly profileCode: string;
  readonly skill: ProfileSkillDto;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: (next: boolean) => void;
}): React.JSX.Element {
  const [helpOpen, setHelpOpen] = React.useState<boolean>(false);
  const base = `skill-${profileCode}-${skill.skillCode}`.replace(/[^a-zA-Z0-9_-]/g, '_');
  const helpId = `${base}-help`;
  const helpText =
    skill.description ?? 'Aucun descriptif fourni pour cette compétence.';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-sm text-foreground">
        <input
          id={base}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="h-4 w-4 rounded border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <label htmlFor={base}>{skill.libelle}</label>
        {/* Aide (l.2826) : survol (`title`) OU clic (bascule). Toujours présente, même sans descriptif. */}
        <button
          type="button"
          aria-label={`Aide sur la compétence ${skill.libelle}`}
          aria-expanded={helpOpen}
          aria-controls={helpId}
          title={helpText}
          onClick={() => setHelpOpen((v) => !v)}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          <span aria-hidden="true">?</span>
        </button>
      </div>
      {helpOpen && (
        <p id={helpId} className="ml-6 text-xs text-muted-foreground">
          {helpText}
        </p>
      )}
    </div>
  );
}
